import { Component, Input, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CountryDescriptor, Country, CountriesByContinent } from '@app/enums/country';
import { Continent, ContinentDescriptor } from '@app/enums/continent';
import { ContinentWithCountries } from './models/contient-with-countries.model';
import { FilteredCountriesByContinent } from './models/filtered-countries-by-continent.model';

@Component({
  standalone: true,
  selector: 'app-geo-location-selector',
  templateUrl: './geo-location-selector.component.html',
  styleUrl: './geo-location-selector.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
})
export class GeoLocationSelectorComponent implements OnInit {
  private continentsWithCountries: ContinentWithCountries[] = [];
  private expandedContinents: Continent[] = [];

  protected readonly geoLocationSearchControl = new FormControl('', { nonNullable: true });
  protected readonly CountryDescriptor = CountryDescriptor;

  @Input() countryCodesControl!: FormControl<Country[]>;
  @ViewChild(MatAutocompleteTrigger) geoLocationAutocompleteTrigger?: MatAutocompleteTrigger;

  public ngOnInit(): void {
    this.continentsWithCountries = (Object.entries(CountriesByContinent) as [Continent, Set<Country>][]).map(
      ([continent, countries]) =>
        ({
          name: continent,
          label: ContinentDescriptor[continent],
          countries: [...countries].sort((a, b) => CountryDescriptor[a].localeCompare(CountryDescriptor[b])),
        }) as ContinentWithCountries,
    );

    this.expandedContinents = this.continentsWithCountries.map((continent) => continent.name);
  }

  protected get filteredContinentsWithCountries(): FilteredCountriesByContinent[] {
    const searchTerm = this.geoLocationSearchControl.value.trim().toLowerCase();

    return this.continentsWithCountries
      .map((continent) => ({
        continentWithCountries: continent,
        filteredCountries: continent.countries.filter(
          (country) =>
            !searchTerm ||
            continent.name.toLowerCase().includes(searchTerm) ||
            continent.label.toLowerCase().includes(searchTerm) ||
            country.toLowerCase().includes(searchTerm) ||
            CountryDescriptor[country].toLowerCase().includes(searchTerm),
        ),
      }))
      .filter((continentWithCountries) => continentWithCountries.filteredCountries.length > 0);
  }

  protected openGeoLocationAutocompletePanel(): void {
    this.geoLocationAutocompleteTrigger?.openPanel();
  }

  protected clearCountrySelection(): void {
    this.countryCodesControl.setValue([]);
  }

  protected clearGeoLocationSearch($event: Event): void {
    $event.stopPropagation();
    this.geoLocationSearchControl.setValue('');
  }

  protected onContinentSelected(continent: ContinentWithCountries): void {
    const selectedCountries = this.countryCodesControl.value || [];

    // If continent was fully selected => unselect it and all of it's countries
    // If continent was not fully selected => merge its countries with the currently selected countries
    const updatedCountries = this.isContinentFullySelected(continent)
      ? selectedCountries.filter((country) => !continent.countries.includes(country))
      : [...new Set([...selectedCountries, ...continent.countries])];

    this.countryCodesControl.setValue(updatedCountries);
    queueMicrotask(() => this.openGeoLocationAutocompletePanel());
  }

  protected isContinentFullySelected(continent: ContinentWithCountries): boolean {
    const selectedCountries = this.countryCodesControl.value || [];
    return continent.countries.every((country) => selectedCountries.includes(country));
  }

  protected isContinentIndeterminate(continent: ContinentWithCountries): boolean {
    const selectedCountries = this.countryCodesControl.value || [];
    const selectedCount = continent.countries.filter((country) => selectedCountries.includes(country)).length;
    return selectedCount > 0 && selectedCount < continent.countries.length;
  }

  protected toggleContinentExpansion(continent: Continent): void {
    this.expandedContinents = this.isContinentExpanded(continent)
      ? this.expandedContinents.filter((value) => value !== continent)
      : [...this.expandedContinents, continent];
  }

  protected isContinentExpanded(continent: Continent): boolean {
    return this.expandedContinents.includes(continent);
  }

  protected shouldShowContinentCountries(continent: Continent): boolean {
    return this.isContinentExpanded(continent) || this.geoLocationSearchControl.value.trim().length > 0;
  }

  protected onCountrySelected(event: MatOptionSelectionChange, selectedCountry: Country): void {
    // If the selection change was not triggered by the user, do nothing
    if (!event.isUserInput) {
      return;
    }

    const selectedCountries = this.countryCodesControl.value || [];
    const updatedCountries = selectedCountries.includes(selectedCountry)
      ? selectedCountries.filter((country) => country !== selectedCountry)
      : [...selectedCountries, selectedCountry];

    this.countryCodesControl.setValue(updatedCountries);
    queueMicrotask(() => this.openGeoLocationAutocompletePanel());
  }

  protected isCountrySelected(country: Country): boolean {
    return (this.countryCodesControl.value || []).includes(country);
  }
}
